import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';

import { MenuItem } from '../../layouts/partials/sidebar/sidebar.component';

const GET_SIDEBAR_MENU = gql`
  query GetSidebarMenu {
    myMenuItems {
      id
      label
      icon
      route
      permissions
      badge
      external
      children {
        id
        label
        icon
        route
        permissions
        badge
        external
      }
    }
  }
`;

const GET_USER_FEATURES = gql`
  query GetUserFeatures {
    myFeatureFlags {
      stockCounting
      adhocCounting
      barcodeScanning
      mobileApp
      maintenanceTasks
      maintenanceTemplates
      approvalWorkflow
      auditTrail
      sapIntegration
      liveSearch
      connectionPool
      userManagement
      qrPairing
      deviceManagement
      companySettings
      odataBuilder
      sapFieldMapping
      customReports
      reportTemplates
      excelExport
      pdfExport
      websocketUpdates
      realTimeSync
      batchOperations
      multiWarehouse
      aiSuggestions
      voiceCommands
      offlineMode
    }
  }
`;

const CHECK_USER_FEATURE = gql`
  query CheckUserFeature($feature: String!) {
    canAccessFeature(feature: $feature)
  }
`;

export interface UserFeature {
  feature: string;
  enabled: boolean;
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private _sidebarMenu$ = new BehaviorSubject<MenuItem[]>([]);
  private _userFeatures$ = new BehaviorSubject<UserFeature[]>([]);
  private _menuLoaded = false;
  private _featuresLoaded = false;

  readonly sidebarMenu$ = this._sidebarMenu$.asObservable();
  readonly userFeatures$ = this._userFeatures$.asObservable();

  constructor(private apollo: Apollo) {}

  /**
   * Load sidebar menu from backend based on user permissions
   */
  loadSidebarMenu(): Observable<MenuItem[]> {
    // Always fetch fresh menu data to avoid cache issues
    // Remove the cache check to ensure we get latest permissions
    // if (this._menuLoaded) {
    //   return this.sidebarMenu$;
    // }

    return this.apollo
      .watchQuery<{ myMenuItems: MenuItem[] }>({
        query: GET_SIDEBAR_MENU,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map(result => {
          const menu = result.data?.myMenuItems || [];
          this._sidebarMenu$.next(menu);
          this._menuLoaded = true;
          return menu;
        }),
        shareReplay(1)
      );
  }

  /**
   * Load user features/capabilities
   */
  loadUserFeatures(): Observable<UserFeature[]> {
    if (this._featuresLoaded) {
      return this.userFeatures$;
    }

    return this.apollo
      .watchQuery<{ myFeatureFlags: any }>({
        query: GET_USER_FEATURES,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map(result => {
          const flags = result.data?.myFeatureFlags || {};
          // Convert feature flags object to array format
          const features: UserFeature[] = Object.keys(flags).map(key => ({
            feature: key,
            enabled: flags[key],
            description: '',
          }));
          this._userFeatures$.next(features);
          this._featuresLoaded = true;
          return features;
        }),
        shareReplay(1)
      );
  }

  /**
   * Check if user has access to a specific feature
   */
  hasFeatureAccess(feature: string): Observable<boolean> {
    return this.apollo
      .watchQuery<{ canAccessFeature: boolean }>({
        query: CHECK_USER_FEATURE,
        variables: { feature },
        fetchPolicy: 'cache-first',
      })
      .valueChanges.pipe(
        map(result => result.data?.canAccessFeature || false),
        shareReplay(1)
      );
  }

  /**
   * Check if a feature is enabled for the current user
   */
  isFeatureEnabled(feature: string): Observable<boolean> {
    return this.userFeatures$.pipe(
      map(features => features.some(f => f.feature === feature && f.enabled))
    );
  }

  /**
   * Get the current sidebar menu
   */
  getSidebarMenu(): MenuItem[] {
    return this._sidebarMenu$.value;
  }

  /**
   * Get current user features
   */
  getUserFeatures(): UserFeature[] {
    return this._userFeatures$.value;
  }

  /**
   * Clear cached permissions (call on logout)
   */
  clearCache(): void {
    this._sidebarMenu$.next([]);
    this._userFeatures$.next([]);
    this._menuLoaded = false;
    this._featuresLoaded = false;
  }

  /**
   * Refresh permissions from backend
   */
  refresh(): void {
    this._menuLoaded = false;
    this._featuresLoaded = false;
    this.loadSidebarMenu().subscribe();
    this.loadUserFeatures().subscribe();
  }
}
